import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown, Lock, Quote, TrendingUp } from "lucide-react";
import { formatDate } from "@/lib/api";

export const Section = ({ title, hint, children, testid }) => (
  <section className="space-y-3" data-testid={testid}>
    <div className="flex items-baseline justify-between gap-3">
      <h2 className="text-base md:text-lg font-semibold">{title}</h2>
      {hint && <span className="text-[11px] text-[#71717A] text-right">{hint}</span>}
    </div>
    {children}
  </section>
);

export const Empty = ({ children, testid }) => <div className="card p-4 text-sm text-[#A1A1AA]" data-testid={testid}>{children}</div>;

// Collapsed-by-default section: tap the header to expand.
export function Fold({ title, summary, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="card overflow-hidden" data-testid={testid}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`${testid}-toggle`} className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-white/[0.02] transition-colors">
        <span className="flex-1 font-semibold text-[15px]">{title}</span>
        {summary != null && <span className="text-xs text-[#71717A] truncate max-w-[45%]">{summary}</span>}
        <ChevronDown size={16} className={`text-[#71717A] shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-200 ease-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          {open && <div className="px-3 pb-3 space-y-2.5 [&_.card]:bg-white/[0.02]" data-testid={`${testid}-content`}>{children}</div>}
        </div>
      </div>
    </section>
  );
}

export function Stat({ label, value, sub, testid }) {
  return (
    <div className="card px-3 py-3" data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#71717A] mb-1.5 truncate">{label}</div>
      <div className="font-display text-2xl font-semibold leading-none">{value}</div>
      {sub && <div className="text-[10.5px] text-[#71717A] mt-1 truncate">{sub}</div>}
    </div>
  );
}

export function WeeklyBars({ weeks }) {
  const max = Math.max(1, ...weeks.map((w) => w.count));
  return (
    <div className="card px-4 py-3" data-testid="analytics-weekly-chart">
      <div className="flex justify-between text-[10px] font-bold uppercase tracking-[0.14em] text-[#71717A] mb-2"><span>Recent activity</span><span className="font-medium normal-case tracking-normal">sessions per week · 12 wks</span></div>
      <div className="flex items-end gap-1.5 h-16">
        {weeks.map((w) => (
          <div key={w.week_start} className="flex-1 flex flex-col items-center justify-end h-full" title={`${formatDate(w.week_start)}: ${w.count}`}>
            {w.count > 0 && <span className="text-[9px] text-[#A1A1AA] mb-1">{w.count}</span>}
            <div className="w-full rounded-sm bg-[#E50914]" style={{ height: `${(w.count / max) * 100}%`, minHeight: w.count ? 4 : 2, opacity: w.count ? 1 : 0.15 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function CountList({ items, testid, empty }) {
  if (!items.length) return <Empty testid={`${testid}-empty`}>{empty}</Empty>;
  const max = items[0].count;
  return (
    <div className="card p-4 space-y-2.5" data-testid={testid}>
      {items.map((t) => (
        <Link key={t.name} to={`/tag/${encodeURIComponent(t.name)}`} className="block" data-testid={`${testid}-${t.name.replace(/\s+/g, "-")}`}>
          <div className="flex justify-between text-sm mb-1"><span className="capitalize">{t.name}</span><span className="text-[#A1A1AA]">{t.count}</span></div>
          <div className="h-1.5 rounded-full bg-white/5"><div className="h-full rounded-full bg-white/70" style={{ width: `${(t.count / max) * 100}%` }} /></div>
        </Link>
      ))}
    </div>
  );
}

export function Evidence({ ev }) {
  return (
    <Link to={`/session/${ev.session_id}`} className="flex gap-2 text-[12.5px] text-[#A1A1AA] hover:text-white" data-testid="analytics-evidence-link">
      <Quote size={12} className="shrink-0 mt-1 text-[#52525B]" />
      <span><span className="italic">"{ev.quote}"</span> <span className="text-[#71717A] whitespace-nowrap">· {formatDate(ev.date)}</span></span>
    </Link>
  );
}

export function InsightCard({ item, tone, badge, testid, children }) {
  const bar = { good: "bg-[#22C55E]", bad: "bg-[#E50914]", focus: "bg-white" }[tone];
  return (
    <div className="card p-4 relative overflow-hidden" data-testid={testid}>
      <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${bar}`} />
      <div className="flex items-start justify-between gap-3">
        <div className="font-semibold leading-snug">{item.title}</div>
        {badge && <span className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] bg-white/5 rounded px-1.5 py-0.5 shrink-0">{badge}</span>}
      </div>
      {(item.detail || item.why) && <p className="text-sm text-[#D4D4D8] mt-1.5">{item.detail || item.why}</p>}
      {children}
      <div className="mt-3 space-y-1.5">{item.evidence?.map((e) => <Evidence key={e.session_id} ev={e} />)}</div>
    </div>
  );
}

export function ProgressCard({ item }) {
  return (
    <div className="card p-4" data-testid="analytics-progress-item">
      <div className="flex items-center gap-2 font-semibold"><TrendingUp size={15} className="text-[#22C55E]" />{item.title}</div>
      {item.detail && <p className="text-sm text-[#D4D4D8] mt-1.5">{item.detail}</p>}
      <div className="mt-3 grid gap-2">
        <div><div className="eyebrow mb-1">Earlier</div><Evidence ev={item.earlier} /></div>
        <div><div className="eyebrow mb-1">Recently</div><Evidence ev={item.recent} /></div>
      </div>
    </div>
  );
}

export function LockedSections() {
  const items = ["Report updates", "Progress over time", "Your BJJ game", "Quiz weak spots"];
  return (
    <div className="card p-4" data-testid="analytics-locked">
      <div className="flex items-center gap-2 font-semibold text-[15px]"><Lock size={14} className="text-[#E50914]" /> Full analysis is part of Pro</div>
      <p className="text-[13px] text-[#A1A1AA] mt-1">Every insight links back to your own sessions and quiz answers.</p>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {items.map((t) => <span key={t} className="rounded-md bg-white/[0.03] border border-[#1F1F23] px-2 py-1 text-[11.5px] text-[#71717A] flex items-center gap-1"><Lock size={10} />{t}</span>)}
      </div>
      <Link to="/pro" className="btn-primary w-full mt-4" data-testid="analytics-upgrade-button">See Pro <ArrowRight size={16} /></Link>
    </div>
  );
}
